import { Flex } from '@sequencehq/core-components'
import { type FC, useCallback, useMemo } from 'react'
import { ReactFlow, ReactFlowProvider, useReactFlow } from '@xyflow/react'
import { useAtomValue, useSetAtom } from 'jotai'
import {
  AddNodeToolbar,
  ConfigEditor,
  ConnectionDropPicker,
  ConnectionLine,
  ConnectionPreviewNode,
  CONNECTION_PREVIEW_NODE_TYPE,
  DefaultEdge,
  VersionPreviewToolbar,
  WorkflowErrors,
  ZoomControls
} from './components'
import { useCanvas, useInsertedNodeShift } from './hooks'
import '@xyflow/react/dist/style.css'
import { usePrefetchAllProducts } from 'common/hooks'
import {
  addNodeAtom,
  isEditingDisabledAtom,
  useResolvedNodeDefinitions
} from 'Automations/automationEditor/atoms'
import { FIT_VIEW_OPTIONS } from 'Automations/automationEditor/constants'

const edgeTypes = { defaultEdge: DefaultEdge }

// Pointer movement (px) below which a press-and-release counts as a click
// rather than a drag. With selectNodesOnDrag={false}, React Flow's defaults
// (nodeClickDistance 0, nodeDragThreshold 1) treat the slightest pointer
// drift during a click as a micro-drag: the click that selects the node is
// suppressed and the node is deselected, so clicking a node fails to open its
// config. Allowing a few pixels of drift makes node selection reliable while
// still recognising deliberate drags. [BADGE-5889]
const NODE_CLICK_TOLERANCE_PX = 8

export const Canvas: FC = () => {
  usePrefetchAllProducts()

  return (
    <Flex
      data-testid="automations.editor.canvas"
      width="100%"
      height="100%"
      position="relative"
      overflow="hidden"
      sx={{
        'svg.react-flow__connectionline': {
          zIndex: 0
        }
      }}
    >
      <ReactFlowProvider>
        <CanvasContent />
      </ReactFlowProvider>
    </Flex>
  )
}

const CanvasContent: FC = () => {
  const canvasProps = useCanvas()
  const isEditingDisabled = useAtomValue(isEditingDisabledAtom)
  const { screenToFlowPosition } = useReactFlow()
  const addNode = useSetAtom(addNodeAtom)
  const definitions = useResolvedNodeDefinitions()

  // Each node type renders via its definition's `editorNode`. Built from the
  // resolved registry (async entries are concrete here) rather than a static
  // module map, so every node type — including the async start nodes — gets its
  // editor component.
  const nodeTypes = useMemo(
    () => ({
      ...Object.fromEntries(
        Object.entries(definitions).map(([type, def]) => [type, def.editorNode])
      ),
      [CONNECTION_PREVIEW_NODE_TYPE]: ConnectionPreviewNode
    }),
    [definitions]
  )

  useInsertedNodeShift()

  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault()
    event.dataTransfer.dropEffect = 'move'
  }, [])

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault()
      if (isEditingDisabled) return

      const data = event.dataTransfer.getData('application/automations-node')
      if (!data) return

      const { nodeType, config, offsetX = 0, offsetY = 0 } = JSON.parse(data)
      const position = screenToFlowPosition({
        x: event.clientX + offsetX,
        y: event.clientY + offsetY
      })

      addNode({ nodeType, config, position })
    },
    [isEditingDisabled, screenToFlowPosition, addNode]
  )

  return (
    <>
      <ReactFlow
        {...canvasProps}
        onDragOver={onDragOver}
        onDrop={onDrop}
        proOptions={{ hideAttribution: true }}
        fitView
        fitViewOptions={FIT_VIEW_OPTIONS}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        connectionLineComponent={ConnectionLine}
        {...(isEditingDisabled
          ? {
              nodesDraggable: false,
              nodesConnectable: false,
              edgesReconnectable: false,
              connectOnClick: false
            }
          : {
              nodesDraggable: true,
              nodesConnectable: true,
              edgesReconnectable: true,
              connectOnClick: true
            })}
        deleteKeyCode={['Backspace', 'Delete']}
        nodeClickDistance={NODE_CLICK_TOLERANCE_PX}
        nodeDragThreshold={NODE_CLICK_TOLERANCE_PX}
        zoomOnDoubleClick={false}
        panOnScroll
        elevateEdgesOnSelect
        // Don't select a node when it's dragged. The config panel (e.g. the
        // reminder node's email composer) renders from selection state, so
        // selecting on drag would open it when the user only meant to
        // reposition the node. Clicking still selects and opens the config.
        selectNodesOnDrag={false}
      />

      <ConfigEditor />
      <WorkflowErrors />
      <ZoomControls />

      <AddNodeToolbar />
      <VersionPreviewToolbar />
      <ConnectionDropPicker />
    </>
  )
}
